import React, { useState, useEffect, useRef } from 'react';
import { t } from '../utils/i18n';
import { createLineChart } from '../charts/lineChart';
import { createDonutChart } from '../charts/donutChart';


const Dashboard = ({ user, onLogout, onNavigate, api }) => {
  const [stats, setStats] = useState({
    darawiishForce: 0,
    policeForce: 0,
    custodialForce: 0,
    systemUsers: 0,
    pendingAllowance: 0,
    pendingPayrollDarawiish: 0,
    pendingPayrollPolice: 0,
    pendingPayrollCustodial: 0,
    biometricStatus: "0/0",
    pendingAllowanceArmy: 0
  });
  const [loading, setLoading] = useState(true);

  const [regions] = useState([
    { name: "Bari", biometric: "120 / 150", discipline: 150, police: 100, military: 150, allowance: "$45.00", payroll: "$45.00", status: "Active", updated: "22-08-2025" },
    { name: "Nugaal", biometric: "120 / 150", discipline: 200, police: 80, military: 200, allowance: "$100.00", payroll: "$100.00", status: "On Leave", updated: "21-08-2025" },
    { name: "Mudug", biometric: "120 / 150", discipline: 130, police: 80, military: 130, allowance: "$150.00", payroll: "$150.00", status: "On Leave", updated: "20-08-2025" },
    { name: "Haylaan", biometric: "120 / 150", discipline: 150, police: 90, military: 150, allowance: "$150.00", payroll: "$150.00", status: "Active", updated: "19-08-2025" },
    { name: "Sanaag", biometric: "120 / 150", discipline: 150, police: 100, military: 150, allowance: "$120.00", payroll: "$120.00", status: "Active", updated: "18-08-2025" }
  ]);

  const lineChartRef = useRef(null);
  const donutChartRef = useRef(null);
  const payrollChartInstance = useRef(null);
  const leaveChartInstance = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const initializeCharts = async () => {
      if (payrollChartInstance.current) {
        try { payrollChartInstance.current.destroy(); } catch (e) {}
      }
      if (leaveChartInstance.current) {
        try { leaveChartInstance.current.destroy(); } catch (e) {}
      }

      // Initialize line chart for payroll
      if (lineChartRef.current) {
        payrollChartInstance.current = createLineChart('payrollChart');
      }

      // Initialize donut chart for leave requests
      if (donutChartRef.current) {
        leaveChartInstance.current = createDonutChart('leaveChart', { users: 450, synced: 300, unsynced: 120 });
      }
    };

    const apiClient = api || {
      get: async (path) => {
        try {
          const url = path.startsWith('/api/') ? path : `/api/v1${path}`;
          const token = localStorage.getItem('token');
          const resp = await fetch(url, {
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            }
          });
          if (!resp.ok) return null;
          return await resp.json();
        } catch (e) {
          console.error('Dashboard fallback api error', e);
          return null;
        }
      }
    };

    // Load dynamic stats from API
    const loadStats = async () => {
      try {
        // Try dedicated aggregated backend endpoint first
        const dashRes = await apiClient.get('/dashboard/stats');
        const dashData = (dashRes && dashRes.data) || dashRes;

        if (dashData && (dashData.darawiish_force !== undefined || dashData.police_force !== undefined || dashData.custodial_force !== undefined)) {
          if (isMounted) {
            setStats({
              darawiishForce: dashData.darawiish_force ?? dashData.military_force ?? 0,
              policeForce: dashData.police_force ?? 0,
              custodialForce: dashData.custodial_force ?? dashData.discipline_force ?? 0,
              systemUsers: dashData.system_users ?? 0,
              pendingAllowance: dashData.pending_allowance ?? 0,
              pendingPayrollDarawiish: dashData.pending_payroll_darawiish ?? dashData.pending_payroll_military ?? 0,
              pendingPayrollPolice: dashData.pending_payroll_police ?? 0,
              pendingPayrollCustodial: dashData.pending_payroll_custodial ?? dashData.pending_payroll_discipline ?? 0,
              biometricStatus: dashData.biometric_status || "0/0",
              pendingAllowanceArmy: dashData.pending_allowance_army ?? 0
            });
            setLoading(false);
          }
          return;
        }

        // Fallback: load and calculate client-side
        const [armyTypesRes, personnelRes, usersRes, usersPersonnelRes, payrollRes, allowancesRes] = await Promise.all([
          apiClient.get('/army_types'),
          apiClient.get('/army_personnel'),
          apiClient.get('/users?per_page=1'),
          apiClient.get('/users?personnel=true&per_page=10000'),
          apiClient.get('/payroll_records'),
          apiClient.get('/allowances?status=pending')
        ]);

        const armyTypes = Array.isArray(armyTypesRes) ? armyTypesRes : (armyTypesRes && armyTypesRes.data) || [];
        const personnel = (personnelRes && (personnelRes.data && personnelRes.data.army_personnel)) || personnelRes || [];
        const usersSummary = usersRes || {};
        const usersPersonnel = (usersPersonnelRes && usersPersonnelRes.users) || (usersPersonnelRes && usersPersonnelRes.data && usersPersonnelRes.data.users) || [];
        const payrollRecords = (payrollRes && payrollRes.data && payrollRes.data.payroll_records) || payrollRes || [];
        const allowances = (allowancesRes && allowancesRes.allowances) || (allowancesRes && allowancesRes.data && allowancesRes.data.allowances) || [];

        const armyTypeMap = {};
        (Array.isArray(armyTypes) ? armyTypes : []).forEach(at => {
          if (at && at.id) armyTypeMap[at.id] = (at.name || '').toLowerCase();
        });

        const isPolice = (name) => /police|ppf/i.test(name);
        const isDarawiish = (name) => /darawiish|darawish|milit|pmf/i.test(name);
        const isCustodial = (name) => /custodial|asluubta|discipline|pcc/i.test(name);

        let police = 0, darawiish = 0, custodial = 0;
        const personnelMap = {};
        (personnel || []).forEach(p => {
          if (p && p.id) personnelMap[p.id] = p;
          const name = armyTypeMap[p.army_type_id] || '';
          if (isPolice(name)) police += 1;
          else if (isDarawiish(name)) darawiish += 1;
          else if (isCustodial(name)) custodial += 1;
        });

        const systemUsers = (usersSummary && (usersSummary.total_count || (usersSummary.data && usersSummary.data.total_count))) || (usersSummary && usersSummary.users && usersSummary.users.length) || 0;

        let pendingPrPolice = 0, pendingPrDarawiish = 0, pendingPrCustodial = 0;
        (Array.isArray(payrollRecords) ? payrollRecords : []).forEach(r => {
          if (r && (!r.status || r.status !== 'finalized')) {
            const p = personnelMap[r.army_personnel_id];
            const name = p ? (armyTypeMap[p.army_type_id] || '') : '';
            if (isPolice(name)) pendingPrPolice += 1;
            else if (isDarawiish(name)) pendingPrDarawiish += 1;
            else if (isCustodial(name)) pendingPrCustodial += 1;
          }
        });

        let pendingAllowancesTotal = 0;
        let pendingAllowancesArmy = 0;
        (Array.isArray(allowances) ? allowances : []).forEach(a => {
          if (a && a.status === 'pending') {
            const amt = parseFloat(a.amount) || 0;
            pendingAllowancesTotal += amt;
            const name = armyTypeMap[a.army_type_id] || '';
            if (isDarawiish(name)) pendingAllowancesArmy += amt;
          }
        });

        const totalPersonnel = usersPersonnel.length || personnel.length || 0;
        const enrolled = usersPersonnel.reduce((acc, u) => acc + (u.army_personnel && (u.army_personnel.biometric_fingerprint || u.army_personnel.biometric_fingerprint === true) ? 1 : 0), 0);

        if (isMounted) {
          setStats({
            darawiishForce: darawiish,
            policeForce: police,
            custodialForce: custodial,
            systemUsers: Number(systemUsers) || 0,
            pendingAllowance: pendingAllowancesTotal,
            pendingPayrollDarawiish: pendingPrDarawiish,
            pendingPayrollPolice: pendingPrPolice,
            pendingPayrollCustodial: pendingPrCustodial,
            biometricStatus: `${enrolled}/${totalPersonnel}`,
            pendingAllowanceArmy: pendingAllowancesArmy
          });
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to load dashboard stats', err);
        if (isMounted) setLoading(false);
      }
    };

    setTimeout(() => {
      initializeCharts();
      loadStats();
    }, 100);

    return () => {
      isMounted = false;
      if (payrollChartInstance.current) {
        try { payrollChartInstance.current.destroy(); } catch (e) {}
      }
      if (leaveChartInstance.current) {
        try { leaveChartInstance.current.destroy(); } catch (e) {}
      }
    };
  }, []);

  const handleCardClick = (route) => {
    if (onNavigate && route) {
      onNavigate(route);
    }
  };

  return (
    <>
      <ul className="dFlex dashFeaturesList">
        <li>
          <div
            className="dashFeaturesBox isClickable"
            onClick={() => handleCardClick('personnel-military')}
            title="View Puntland Darawiish Force"
          >
            <div className="dashFeaturesTxt">
              Puntland Darawiish Force
              <span className="txt30 fwBold colorBlack mt_12">{stats.darawiishForce.toLocaleString()}</span>
            </div>
            <div className="dashFeaturesIcon iconDarawiish">
              <img src="/images/Darawish_army.jpg" alt="Puntland Darawiish Force" />
            </div>
          </div>
        </li>
        <li>
          <div
            className="dashFeaturesBox isClickable"
            onClick={() => handleCardClick('personnel-police')}
            title="View Puntland Police Force"
          >
            <div className="dashFeaturesTxt">
              Puntland Police Force
              <span className="txt30 fwBold colorBlack mt_12">{stats.policeForce.toLocaleString()}</span>
            </div>
            <div className="dashFeaturesIcon iconPolice">
              <img src="/images/c-h-logo.png" alt="Puntland Police Force" />
            </div>
          </div>
        </li>
        <li>
          <div
            className="dashFeaturesBox isClickable"
            onClick={() => handleCardClick('personnel-discipline')}
            title="View Puntland Custodial Corps (Asluubta)"
          >
            <div className="dashFeaturesTxt">
              Puntland Custodial Corps (Asluubta)
              <span className="txt30 fwBold colorBlack mt_12">{stats.custodialForce.toLocaleString()}</span>
            </div>
            <div className="dashFeaturesIcon iconCustodial">
              <img src="/images/pmfc-custodial-logo.png" alt="Puntland Custodial Corps" />
            </div>
          </div>
        </li>
        <li>
          <div
            className="dashFeaturesBox isClickable"
            onClick={() => handleCardClick('users')}
            title="View System Users"
          >
            <div className="dashFeaturesTxt">
              System Users
              <span className="txt30 fwBold colorBlack mt_12">{stats.systemUsers.toLocaleString()}</span>
            </div>
            <div className="dashFeaturesIcon iconUsers">
              <img src="/images/navIcon7.png" alt="System Users" />
            </div>
          </div>
        </li>
        <li>
          <div
            className="dashFeaturesBox isClickable"
            onClick={() => handleCardClick('payroll')}
            title="View Allowances"
          >
            <div className="dashFeaturesTxt">
              Pending Allowance
              <span className="txt30 fwBold colorBlack mt_12">
                ${Number(stats.pendingAllowance || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
              </span>
            </div>
            <div className="dashFeaturesIcon iconAllowance">
              <img src="/images/navIcon5.png" alt="Pending Allowance" />
            </div>
          </div>
        </li>
        <li>
          <div
            className="dashFeaturesBox isClickable"
            onClick={() => handleCardClick('payroll-military')}
            title="View Darawiish Payroll"
          >
            <div className="dashFeaturesTxt">
              Pending Payroll Darawiish
              <span className="txt30 fwBold colorBlack mt_12">{stats.pendingPayrollDarawiish.toLocaleString()}</span>
            </div>
            <div className="dashFeaturesIcon iconDarawiish">
              <img src="/images/Darawish_army.jpg" alt="Pending Payroll Darawiish" />
            </div>
          </div>
        </li>
        <li>
          <div
            className="dashFeaturesBox isClickable"
            onClick={() => handleCardClick('payroll-police')}
            title="View Police Payroll"
          >
            <div className="dashFeaturesTxt">
              Pending Payroll Police
              <span className="txt30 fwBold colorBlack mt_12">{stats.pendingPayrollPolice.toLocaleString()}</span>
            </div>
            <div className="dashFeaturesIcon iconPolice">
              <img src="/images/c-h-logo.png" alt="Pending Payroll Police" />
            </div>
          </div>
        </li>
        <li>
          <div
            className="dashFeaturesBox isClickable"
            onClick={() => handleCardClick('payroll-discipline')}
            title="View Custodial Payroll"
          >
            <div className="dashFeaturesTxt">
              Pending Payroll Custodial
              <span className="txt30 fwBold colorBlack mt_12">{stats.pendingPayrollCustodial.toLocaleString()}</span>
            </div>
            <div className="dashFeaturesIcon iconCustodial">
              <img src="/images/pmfc-custodial-logo.png" alt="Pending Payroll Custodial" />
            </div>
          </div>
        </li>
        <li>
          <div
            className="dashFeaturesBox isClickable"
            onClick={() => handleCardClick('biometric_devices')}
            title="View Biometric Status"
          >
            <div className="dashFeaturesTxt">
              Biometric Status
              <span className="txt30 fwBold colorBlack mt_12">{stats.biometricStatus}</span>
            </div>
            <div className="dashFeaturesIcon iconBiometric">
              <img src="/images/fingerprint.png" alt="Biometric Status" />
            </div>
          </div>
        </li>
        <li>
          <div
            className="dashFeaturesBox isClickable"
            onClick={() => handleCardClick('payroll-military')}
            title="View Army Allowances"
          >
            <div className="dashFeaturesTxt">
              Pending Allowance Army
              <span className="txt30 fwBold colorBlack mt_12">
                ${Number(stats.pendingAllowanceArmy || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
              </span>
            </div>
            <div className="dashFeaturesIcon iconArmyAllowance">
              <img src="/images/navIcon5.png" alt="Pending Allowance Army" />
            </div>
          </div>
        </li>
      </ul>

      <div className="mt_28 dashGraphsSec">
        <div className="dashGraphs dashGraphsPayroll">
          <div className="dFlex mb_32 dashGraphsHd">
            <h2 className="txt24 fwSemiBold colorBlack">Payroll Overview</h2>
            <div className="dFlex mOpt">
              <a href="#" className="active">1M</a>
              <a href="#">3M</a>
              <a href="#">6M</a>
              <a href="#">1Y</a>
            </div>
          </div>
          <div className="dashGraph">
            <canvas ref={lineChartRef} id="payrollChart" width="400" height="200"></canvas>
          </div>
        </div>
        <div className="dashGraphs dashGraphsLeaveReq">
          <div className="dFlex mb_32 dashGraphsHd">
            <h2 className="txt24 fwSemiBold colorBlack">Leave Requests Status</h2>
            <div className="dropdown dropdownLRS">
              <button className="dropdown-toggle" type="button">
                <svg width="16" height="4" viewBox="0 0 16 4" fill="none">
                  <circle cx="2" cy="2" r="2" fill="" />
                  <circle cx="8" cy="2" r="2" fill="" />
                  <circle cx="14" cy="2" r="2" fill="" />
                </svg>
              </button>
            </div>
          </div>
          <ul className="dFlex describeColor mb_32">
            <li className="dcColor1">
              Approved
              <span className="txt24 fwBold mt_4">450</span>
            </li>
            <li className="dcColor2">
              In Process
              <span className="txt24 fwBold mt_4">300</span>
            </li>
            <li className="dcColor3">
              Pending
              <span className="txt24 fwBold mt_4">120</span>
            </li>
          </ul>
          <div className="imgCenter dashGraph">
            <canvas ref={donutChartRef} id="leaveChart" width="300" height="300"></canvas>
          </div>
        </div>
      </div>

      <div className="mt_40 baseTableWrap">
        <div className="dFlex baseTableHd mb_16">
          <h2 className="txt20 fwMedium colorBlack">Regions Overview</h2>
          <div className="dFlex baseTableTR">
            <div className="searchBox">
              <input type="text" placeholder="Search by Name, Location" />
              <button type="button">
                <img src="/images/search1.png" alt="" />
              </button>
            </div>
            <div className="dropdown dropdownSortBy">
              <button className="dropdown-toggle" type="button">
                {t('sort_by')}
              </button>
            </div>
          </div>
        </div>
        <div className="baseTableContainer">
          <table className="baseTable">
            <thead>
              <tr>
                <th>Region</th>
                <th>Biometric Enrollment</th>
                <th>Discipline Force Count</th>
                <th>Police Force Count</th>
                <th>Military Force Count</th>
                <th>Allowance Pending</th>
                <th>Payroll Pending</th>
                <th>Status</th>
                <th>Last Updated</th>
              </tr>
            </thead>
            <tbody>
              {regions.map((region, index) => (
                <tr key={index}>
                  <td>{region.name}</td>
                  <td>{region.biometric}</td>
                  <td>{region.discipline}</td>
                  <td>{region.police}</td>
                  <td>{region.military}</td>
                  <td>{region.allowance}</td>
                  <td>{region.payroll}</td>
                  <td>
                    <span className={`status ${region.status === 'Active' ? 'green' : 'red'}`}>
                      {region.status}
                    </span>
                  </td>
                  <td>{region.updated}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
